/**
 * Release Ritual - Day 3 Interactive Element
 * Writing and releasing emotional content.
 */

import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Flame, Check } from "lucide-react";

const ReleaseRitualPage = () => {
  const navigate = useNavigate();
  const { journeyId, dayNumber } = useParams();
  const [phase, setPhase] = useState("intro"); // intro, writing, release, complete
  const [text, setText] = useState("");
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes
  const [timerActive, setTimerActive] = useState(false);

  useEffect(() => {
    if (!timerActive || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timerActive, timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Intro phase
  if (phase === "intro") {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex flex-col">
        <div className="p-6">
          <button
            onClick={() => navigate(`/balance/${journeyId}/day/${dayNumber}`)}
            className="text-white/60"
          >
            <ArrowLeft size={24} />
          </button>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-500/20 to-red-500/20 flex items-center justify-center mb-8"
          >
            <Flame size={36} className="text-orange-400" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-2xl font-light text-white mb-4"
          >
            Release Ritual
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-white/50 mb-8 max-w-sm leading-relaxed"
          >
            You're going to write everything you're feeling right now. 
            No filter. No editing. Just let it pour out.
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-white/30 text-sm mb-12"
          >
            You'll have 3 minutes. Then you'll release it.
          </motion.p>

          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setPhase("writing");
              setTimerActive(true);
            }}
            className="px-10 py-4 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-medium"
          >
            Begin Writing
          </motion.button>
        </div>
      </div>
    );
  }

  // Writing phase
  if (phase === "writing") {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex flex-col">
        {/* Timer header */}
        <div className="p-6 flex items-center justify-between">
          <button
            onClick={() => navigate(`/balance/${journeyId}/day/${dayNumber}`)}
            className="text-white/40 text-sm"
          >
            Exit
          </button>
          <div className={`font-mono text-lg ${timeLeft <= 30 ? "text-orange-400" : "text-white/60"}`}>
            {formatTime(timeLeft)}
          </div>
        </div>

        {/* Prompt */}
        <div className="px-6 pb-4 text-center">
          <p className="text-white/40 text-sm italic">
            Write everything you're feeling… no filter… just let it out
          </p>
        </div>

        {/* Writing area */}
        <div className="flex-1 px-6 pb-6">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Start writing..."
            autoFocus
            className="w-full h-full bg-white/5 rounded-2xl p-6 text-white placeholder-white/20 resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/30 text-lg leading-relaxed"
            style={{ minHeight: "300px" }}
          />
        </div>

        {/* Done button */}
        <div className="p-6">
          <button
            onClick={() => {
              setTimerActive(false);
              setPhase("release");
            }}
            disabled={text.length < 10}
            className={`w-full py-4 rounded-xl font-medium transition-all ${
              text.length >= 10
                ? "bg-gradient-to-r from-orange-500 to-red-500 text-white"
                : "bg-white/5 text-white/30"
            }`}
          >
            I'm Done Writing
          </button>
        </div>
      </div>
    );
  }

  // Release phase
  if (phase === "release") {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center px-8 text-center">
        <motion.div
          initial={{ scale: 1 }}
          animate={{ 
            scale: [1, 1.1, 0],
            opacity: [1, 0.8, 0],
            rotate: [0, 5, -5, 0]
          }}
          transition={{ duration: 3, ease: "easeInOut" }}
          onAnimationComplete={() => {
            setTimeout(() => setPhase("complete"), 500);
          }}
          className="relative"
        >
          {/* Paper representation */}
          <div className="w-64 h-80 bg-white/10 rounded-lg p-4 overflow-hidden relative">
            <p className="text-white/30 text-sm text-left line-clamp-6">
              {text.substring(0, 200)}...
            </p>
            
            {/* Fire overlay */}
            <motion.div
              initial={{ height: "0%" }}
              animate={{ height: "100%" }}
              transition={{ duration: 2.5, ease: "easeIn" }}
              className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-orange-500 via-red-500 to-transparent"
            />
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="text-white/40 text-lg mt-8"
        >
          Releasing...
        </motion.p>
      </div>
    );
  }

  // Complete phase
  return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center px-8 text-center">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring" }}
        className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mb-8"
      >
        <Check size={36} className="text-emerald-400" />
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-2xl font-light text-white/90 mb-4"
      >
        You've done the work
      </motion.h2>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-white/40 mb-12 max-w-sm"
      >
        Whatever needed to move... you gave it space. That's enough.
      </motion.p>

      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        onClick={() => navigate(`/balance/${journeyId}/day/${dayNumber}`)}
        className="px-8 py-3 rounded-full bg-white/10 text-white/80 hover:bg-white/20 transition-colors"
      >
        Return to Day {dayNumber}
      </motion.button>
    </div>
  );
};

export default ReleaseRitualPage;
